import { FilterInputType, FilterInputTemplate } from "@/app/home/components/filter/FilterBar";
import { ReportTableConfigs } from "@/app/home/components/report/ReportTable";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { WaterUserStatus, WaterUserDepositStatus } from "@/core/enums/global.enums";
import { contractStatusPipe, contractStatusTagsPipe } from "@/core/pipes/contract-status.pipe";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { AddressRepo } from "@/core/repos/address.repo";
import { DatabaseRecord } from "@/core/repos/table.repo";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import container from "@/lib/di/container";
import dayjs from "dayjs";

const contractRepo = container.get(ContractRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const addressRepo = container.get(AddressRepo);

export const CONTRACT_REPORT_CONFIGS: {name: string, props: ReportTableConfigs<DatabaseRecord, null>} = {
    name: "Báo cáo Hợp đồng",
    props: {
        columns: [
            { dataIndex: "code", name: "Mã HĐ" },
              { dataIndex: "name", name: "Đại diện HĐ" },
              { dataIndex: "organization", name: "Tên tổ chức" },
              { dataIndex: "userName", name: "TK đại diện" },
              { dataIndex: "tollAreaName", name: "Khu vực" },
              { dataIndex: "unitTypeName", name: "Loại hình đơn vị" },
              { dataIndex: "waterMeterCode", name: "Mã đồng hồ nước" },
              {
                dataIndex: "depositAmount",
                name: "Số tiền đã nộp",
                pipe: formatCurrencyPipe,
              },
              {
                dataIndex: "lastReadDate",
                name: "Ngày đọc cuối",
                pipe: formatDateTimePipe,
              },
              { dataIndex: "lastWaterMeterNumber", name: "Chỉ số cuối" },
              { dataIndex: 'depositStatus', name: 'Trạng thái thu', pipe: depositStatusPipe },
              {
                dataIndex: "status",
                name: "Trạng thái",
                pipe: contractStatusTagsPipe,
                fixed: "right",
              },
        ],
        filterTemplates:[
            {
                dataIndex: "search",
                label: "Tên/ Mã hợp đồng",
                placeholder: "Nhập Tên/ Mã hợp đồng",
                type: FilterInputType.text,
            },
            {
                dataIndex: "note",
                label: "Ghi chú",
                placeholder: "Nhập ghi chú",
                type: FilterInputType.text,
            },
            {
                dataIndex: "meterCode",
                label: "Mã đồng hồ",
                placeholder: "Nhập mã đồng hồ",
                type: FilterInputType.text,
            },
            {
                dataIndex: "phone",
                label: "Số điện thoại",
                placeholder: "Nhập số điện thoại",
                type: FilterInputType.text,
            },
            {
                dataIndex: "identityNo",
                label: "CCCD",
                placeholder: "Nhập CCCD",
                type: FilterInputType.text,
            },
            {
                dataIndex: "hasMeter",
                label: "Trạng thái lắp đồng hồ",
                placeholder: "Chọn Trạng thái lắp đồng hồ",
                type: FilterInputType.select,
                options: [
                    {
                    name: "Có đồng hồ",
                    value: true,
                    },
                    {
                    name: "Chưa có đồng hồ",
                    value: false,
                    },
                ],
            },
            {
                dataIndex: "status",
                label: "Trạng thái hoạt động",
                placeholder: "Chọn Trạng thái hoạt động",
                type: FilterInputType.select,
                options: [
                    {
                    name: contractStatusPipe(WaterUserStatus.Processing),
                    value: WaterUserStatus.Processing,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.Active),
                    value: WaterUserStatus.Active,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.TempDisabled),
                    value: WaterUserStatus.TempDisabled,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.Disabled),
                    value: WaterUserStatus.Disabled,
                    },
                ],
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "provinceId",
                label: "Tỉnh/ TP",
                placeholder: "Chọn Tỉnh/ TP",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "districtId",
                label: "Quận/ Huyện",
                placeholder: "Chọn Quận/ Huyện",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "wardId",
                label: "Xã/ Phường",
                placeholder: "Chọn Xã/ Phường",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "depositStatus",
                label: "Trạng thái thu cọc",
                placeholder: "Chọn trạng thái thu cọc",
                type: FilterInputType.select,
                options: Object.keys(WaterUserDepositStatus).map((key) => ({name: depositStatusPipe(key), value: key})),
            },
        ],
        handleExport: async (filterMap) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const query: any = {};
            filterMap.forEach(filter => {
                if (filter.value) {
                    query[filter.key] = filter.value
                }
            })
            const blobData = await contractRepo.exportExcel(query);
            if (blobData) {
                GlobalUtils.downloadXLSX(blobData, `DanhSachHopDongNuoc-${dayjs().format("DDMMYYYY-hhmm")}`)
            };
        },
        init: async (filterTemplates) => {
            const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({name: area.name, value: area.id}));
            const provinces = (await addressRepo.getAllProvinces()).map(data => ({ name: data[1].name, value: data[0] }));
            const templates: FilterInputTemplate[] = [...filterTemplates];
            const tollAreaTemplate = templates.find(template => template.dataIndex === "tollAreaId");
            if (tollAreaTemplate) {
                tollAreaTemplate.options = tollAreas;
            }
            const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
            if (provinceTemplate) {
                provinceTemplate.options = provinces;
            }

            return {
                filterTemplates: templates
            }
        },
        onFilterChanged: async (map, changes, filterTemplates) => {
            let isDirty = false;
            const templates: FilterInputTemplate[] = [...filterTemplates];
    
            if (changes.find(e => e === "provinceId")) {
                isDirty = true;
                const districts = (await addressRepo.getAllDistricts(map.get("provinceId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
                const districtTemplate = templates.find(template => template.dataIndex === "districtId");
                if (districtTemplate) {
                    map.delete("districtId");
                    districtTemplate.options = districts;
                }
                const wardTemplate = templates.find(template => template.dataIndex === "wardId");
                if (wardTemplate) {
                    map.delete("wardId");
                    wardTemplate.options = [];
                }
            }
    
            if (changes.find(e => e === "districtId")&&!changes.find(e => e === "provinceId")) {
                isDirty = true;
                const wards = (await addressRepo.getAllWards(map.get("districtId")?.value??"")).map(data => ({name: data[1].name, value: data[0]}));
                const wardTemplate = templates.find(template => template.dataIndex === "wardId");
                if (wardTemplate) {
                    map.delete("wardId");
                    wardTemplate.options = wards;
                }
            }
    
            if (isDirty) {
                return {
                    filterTemplates: templates,
                    filterMap: map
                }
            };
            return {
                filterTemplates: undefined
            }
        },
        repository: contractRepo,
    }
}